import { useAuthStore } from '../stores/auth.store';
import type {
  AuthResponse,
  Story,
  StoryDetailResponse,
  StoriesResponse,
  AnonymousJoinResponse,
  AnonymousDetailResponse,
  Comic,
  ComicListItem,
  Adventure,
  ReactionEmoji,
  ReactionState,
  ReactionTargetType,
} from '@storychain/shared';

export interface ExploreComicsParams {
  theme?: string;
  sort?: 'popular' | 'recent';
  period?: 'day' | 'week' | 'month' | 'all';
  page?: number;
  limit?: number;
  language?: string;
}

export interface ExploreStoriesParams {
  sort?: 'popular' | 'recent';
  period?: 'day' | 'week' | 'month' | 'all';
  page?: number;
  limit?: number;
  language?: string;
}

export interface ExploreStoriesResponse {
  stories: import('@storychain/shared').StoryListItem[];
  total: number;
  page: number;
}

export interface ExploreComicsResponse {
  comics: ComicListItem[];
  total: number;
  page: number;
}

const API_URL = '/api';

class ApiClient {
  private getHeaders(): HeadersInit {
    const token = useAuthStore.getState().token;
    return {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
    };
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const response = await fetch(`${API_URL}${endpoint}`, {
      ...options,
      headers: {
        ...this.getHeaders(),
        ...options.headers,
      },
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Request failed' }));
      throw new Error(error.error || 'Request failed');
    }

    return response.json();
  }

  // Auth
  async requestCode(email: string): Promise<{ success: boolean }> {
    return this.request('/auth/request-code', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  }

  async verifyCode(email: string, code: string): Promise<AuthResponse> {
    return this.request('/auth/verify', {
      method: 'POST',
      body: JSON.stringify({ email, code }),
    });
  }

  async loginWithGoogle(idToken: string): Promise<AuthResponse> {
    return this.request('/auth/google', {
      method: 'POST',
      body: JSON.stringify({ idToken }),
    });
  }

  async fetchMeWithToken(token: string): Promise<{ user: AuthResponse['user'] }> {
    return this.request('/auth/me', {
      headers: { Authorization: `Bearer ${token}` },
    });
  }

  // Stories
  async getStoryByCode(code: string): Promise<{ story: Story }> {
    return this.request(`/stories/code/${code}`);
  }

  async joinStory(code: string): Promise<{ story: Story }> {
    return this.request(`/stories/code/${code}/join`, {
      method: 'POST',
      body: JSON.stringify({}),
    });
  }

  async getStoryDetail(id: string): Promise<StoryDetailResponse> {
    return this.request(`/stories/${id}`);
  }

  async addEntry(storyId: string, text: string): Promise<{
    story: Story;
    isCompleted: boolean;
    finalStory?: string;
  }> {
    return this.request(`/stories/${storyId}/entries`, {
      method: 'POST',
      body: JSON.stringify({ text }),
    });
  }

  // Account
  async deleteAccount(): Promise<{ success: boolean }> {
    return this.request('/auth/account', {
      method: 'DELETE',
    });
  }

  // ============================================
  // Anonymous endpoints (no auth required)
  // ============================================

  async joinStoryAnonymous(
    code: string,
    anonymousId: string,
    displayName: string
  ): Promise<AnonymousJoinResponse> {
    return this.request(`/stories/code/${code}/join-anonymous`, {
      method: 'POST',
      body: JSON.stringify({ anonymousId, displayName }),
    });
  }

  async getStoryDetailAnonymous(
    id: string,
    anonymousId: string
  ): Promise<AnonymousDetailResponse> {
    return this.request(`/stories/${id}/detail-anonymous`, {
      method: 'POST',
      body: JSON.stringify({ anonymousId }),
    });
  }

  async addEntryAnonymous(
    storyId: string,
    anonymousId: string,
    text: string
  ): Promise<{ story: Story; isCompleted: boolean; finalStory?: string }> {
    return this.request(`/stories/${storyId}/entries-anonymous`, {
      method: 'POST',
      body: JSON.stringify({ anonymousId, text }),
    });
  }

  // Public completed story (no auth required)
  async getCompletedStory(id: string): Promise<StoryDetailResponse> {
    return this.request(`/stories/${id}/completed`);
  }

  // Comics (public)
  async getComicByShareCode(
    shareCode: string,
  ): Promise<{ comic: Comic; isLiked: boolean; myReaction: ReactionEmoji | null }> {
    return this.request(`/comics/s/${shareCode}`);
  }

  async getFeaturedComics(): Promise<{ comics: ComicListItem[] }> {
    return this.request('/comics/featured');
  }

  async getExploreComics(params: ExploreComicsParams = {}): Promise<ExploreComicsResponse> {
    const qs = new URLSearchParams();
    if (params.theme) qs.set('theme', params.theme);
    if (params.sort) qs.set('sort', params.sort);
    if (params.period) qs.set('period', params.period);
    if (params.page) qs.set('page', String(params.page));
    if (params.limit) qs.set('limit', String(params.limit));
    if (params.language) qs.set('language', params.language);
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return this.request(`/comics/explore${suffix}`);
  }

  async getLanguages(): Promise<{ languages: { code: string; count: number }[] }> {
    return this.request('/languages');
  }

  async getExploreStories(params: ExploreStoriesParams = {}): Promise<ExploreStoriesResponse> {
    const qs = new URLSearchParams();
    if (params.sort) qs.set('sort', params.sort);
    if (params.period) qs.set('period', params.period);
    if (params.page) qs.set('page', String(params.page));
    if (params.limit) qs.set('limit', String(params.limit));
    if (params.language) qs.set('language', params.language);
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return this.request(`/stories/explore${suffix}`);
  }

  async updateStory(id: string, data: { visibility?: 'public' | 'unlisted' }): Promise<{ story: Story }> {
    return this.request(`/stories/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  // Reactions (auth)
  async setReaction(
    target: ReactionTargetType,
    id: string,
    emoji: ReactionEmoji | null,
  ): Promise<ReactionState> {
    const prefix = target === 'story' ? 'stories' : 'comics';
    return this.request(`/${prefix}/${id}/react`, {
      method: 'POST',
      body: JSON.stringify({ emoji }),
    });
  }

  // Library (auth)
  async getMyComics(): Promise<{ comics: ComicListItem[] }> {
    return this.request('/comics');
  }

  async getMyStories(): Promise<StoriesResponse> {
    return this.request('/stories');
  }

  async getMyAdventures(): Promise<{ adventures: Adventure[] }> {
    return this.request('/adventures');
  }
}

export const api = new ApiClient();
